import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { IconArrowLeft, IconCopy, IconCheck, IconX } from "@tabler/icons-react";
import { getRun, gradeRun, reanalyzeRun } from "../services/api";
import VendorBadge, { vendorOfSystem } from "../components/VendorBadge";
import CommitLink from "../components/CommitLink";
import RedFlag from "../components/RedFlag";
import TranscriptMCQ from "../components/TranscriptMCQ";
import RootCauseTab from "../components/RootCauseTab";
import { buildRootCauseView, qaPairIndex, POLL_MS } from "../rootcauseModel";
import {
  buildQAPairs, copyTranscript, copyFacts, groupFacts, isPresetFact, nodeLabel, fmtFactValue,
} from "../transcriptModel";

const fmtWhen = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};
const pctChip = (c, t, p) => (t == null || p == null
  ? <span className="muted">—</span>
  : <span className={`chip ${p >= 80 ? "ok" : p >= 50 ? "warn" : "bad"}`}>{c}/{t} ({p}%)</span>);
const TIER_CLS = { TOP1: "ok", TOP3: "ok", TOP5: "warn", DNM: "n", MISS: "bad" };
const tierChip = (tier) => tier ? <span className={`chip ${TIER_CLS[tier] || "n"}`}>{tier}</span> : <span className="muted">—</span>;
const secs = (ms) => (ms != null ? (ms / 1000).toFixed(1) + "s" : "—");

const NOTE_FIELDS = [["PMH", "pmh"], ["PSH", "psh"], ["Medications", "medications"],
  ["Allergies", "allergies"], ["Family history", "fh"], ["Social history", "sh"]];
const rosLines = (ros) => {
  const out = [];
  for (const [sys, s] of Object.entries(ros || {})) {
    const pos = (s?.positives || []).join(", "), neg = (s?.negatives || []).join(", ");
    if (pos || neg) out.push({ sys, pos, neg });
  }
  return out;
};

// --- plain-text builders for the copy buttons ---
const copyNote = (note, diff) => {
  const parts = [];
  if (note) {
    if (note.title) parts.push(note.title);
    if (note.one_liner) parts.push(note.one_liner);
    if (note.hpi) parts.push(`HPI: ${note.hpi}`);
    const ros = rosLines(note.ros);
    if (ros.length) parts.push("ROS:\n" + ros.map((r) => `  ${r.sys}: +[${r.pos}] -[${r.neg}]`).join("\n"));
    for (const [label, key] of NOTE_FIELDS) if (note[key]) parts.push(`${label}: ${note[key]}`);
    if ((note.to_discuss_with_provider || []).length) parts.push("To discuss: " + note.to_discuss_with_provider.join("; "));
  }
  if (diff) {
    const dl = ["DIFFERENTIAL",
      "Consider: " + (diff.diagnoses_to_consider || []).map((x) => `${x.diagnosis} (${x.likelihood}%)`).join(", ")];
    if ((diff.do_not_miss || []).length) dl.push("Do not miss: " + diff.do_not_miss.map((x) => `${x.diagnosis} (${x.likelihood}%)`).join(", "));
    if ((diff.next_steps || []).length) dl.push("Next steps: " + diff.next_steps.map((x) => x.step).join(", "));
    parts.push(dl.join("\n"));
  }
  return parts.join("\n\n");
};

const copyEval = (ev) => {
  if (!ev) return "";
  const parts = [`Grader: ${ev.evaluator_model || ""}`];
  const dx = ev.diagnosis;
  const secs = [["Red Flags", ev.redflags], ["Must Capture", ev.must_capture], ["Must-capture (legacy)", ev.rubric]];
  for (const [label, rub] of secs) {
    if (rub?.error) parts.push(`${label}: ERROR ${rub.detail}`);
    else if (rub) {
      parts.push(`${label}: ${rub.captured}/${rub.total} (${rub.score_pct}%)`);
      for (const it of (rub.items || [])) parts.push(`  ${it.captured ? "[x]" : "[ ]"} ${it.item} — ${it.rationale}`);
    }
  }
  if (dx?.error) parts.push(`Diagnosis: ERROR ${dx.detail}`);
  else if (dx) {
    parts.push(`Diagnosis: ${dx.tier}${dx.rank ? ` (rank ${dx.rank})` : ""} — goal: ${dx.goal}${dx.matched_label ? `, matched: ${dx.matched_label}` : ""}`);
    if (dx.rationale) parts.push(`  ${dx.rationale}`);
  }
  return parts.join("\n");
};

// Facts tab value: booleans are the MCQ answer state — true (option selected) bold green, false muted.
const factValueCls = (v) => (v === true ? "fact-true" : v === false ? "fact-false" : undefined);

function CopyBtn({ label, copied, onCopy, disabled }) {
  return (
    <button className="btn sm" onClick={onCopy} disabled={disabled} title="Copy to clipboard">
      {copied === label ? <><IconCheck size={13} /> Copied</> : <><IconCopy size={13} /> Copy</>}
    </button>
  );
}

export default function RunDetail() {
  const { runId } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [grading, setGrading] = useState(false);
  const [copied, setCopied] = useState("");
  const [rightTab, setRightTab] = useState("transcript");   // right column: transcript | facts | rootcause
  const [hl, setHl] = useState(null);                       // {i, at}: highlighted Q/A pair ("View in transcript")
  const [reanalyzing, setReanalyzing] = useState(false);
  const [rcErr, setRcErr] = useState("");
  const qaRef = useRef(null);

  const load = () => getRun(runId).then(setD).catch(() => setD(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [runId]);

  const pairs = useMemo(() => buildQAPairs(d?.payload?.transcript), [d]);
  const rcView = useMemo(() => buildRootCauseView(d || null), [d]);

  // Root cause: while the analysis is pending (or the run is not graded yet), re-fetch the run every 3 s.
  // Stops when the status is terminal, and on unmount / run change.
  useEffect(() => {
    if (!rcView.poll) return undefined;
    let alive = true, busy = false;
    const t = setInterval(() => {
      if (busy) return;
      busy = true;
      getRun(runId).then((x) => { if (alive) setD(x); }).catch(() => {}).finally(() => { busy = false; });
    }, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [rcView.poll, runId]);

  // "View in transcript": scroll to the highlighted pair, then clear the highlight.
  useEffect(() => {
    if (hl == null || rightTab !== "transcript") return undefined;
    const el = qaRef.current?.querySelector(`[data-qa="${hl.i}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "center", behavior: "smooth" });
    const t = setTimeout(() => setHl(null), 2400);
    return () => clearTimeout(t);
  }, [hl, rightTab]);

  const viewInTranscript = (tidx) => {
    const i = qaPairIndex(pairs, tidx);
    setRightTab("transcript");
    setHl(i >= 0 ? { i, at: Date.now() } : null);
  };
  const reanalyze = async () => {
    setReanalyzing(true); setRcErr("");
    try {
      const rc = await reanalyzeRun(runId);
      setD((prev) => (prev ? { ...prev, payload: { ...(prev.payload || {}), rootcause_json: rc } } : prev));
    } catch { setRcErr("Re-analyze request failed."); }
    finally { setReanalyzing(false); }
  };

  const copy = async (label, text) => {
    try { await navigator.clipboard.writeText(text); setCopied(label); setTimeout(() => setCopied(""), 1400); }
    catch { /* clipboard unavailable */ }
  };
  const regrade = async () => {
    setGrading(true);
    try { await gradeRun(runId); await load(); } finally { setGrading(false); }
  };

  if (d === false) return <div className="page-head"><h2>Run not found</h2></div>;
  if (!d) return <div className="page-head"><h2>Run…</h2></div>;
  const p = d.payload || {};
  const note = p.soap_note_json, diff = p.differential_json, ev = p.eval_json;

  return (
    <>
      <div className="page-head" style={{ justifyContent: "space-between" }}>
        <div className="flex" style={{ gap: 10 }}>
          <button className="btn sm" onClick={() => nav("/runs")}><IconArrowLeft size={14} /> Back</button>
          <h2 style={{ margin: 0 }}>Run {runId.slice(0, 8)}</h2>
          <VendorBadge vendor={vendorOfSystem(d.system)} />
          <span className={`chip ${d.status === "complete" ? "ok" : "bad"}`}>{d.status}</span>
        </div>
      </div>

      <div className="run-cols">
        {/* LEFT — clinical output + evaluation + provenance */}
        <div className="run-col">
          <div className="card">
            <div className="between"><h3>Note &amp; differential</h3>
              <CopyBtn label="note" copied={copied} onCopy={() => copy("note", copyNote(note, diff))} disabled={!note && !diff} /></div>
            {note ? (
              <>
                <div style={{ fontWeight: 600, color: "var(--head)" }}>{note.title}</div>
                <div className="mono" style={{ margin: "4px 0 10px" }}>{note.one_liner}</div>
                {note.hpi && <p style={{ whiteSpace: "pre-wrap", marginTop: 0 }}>{note.hpi}</p>}
                {rosLines(note.ros).length > 0 && (
                  <div style={{ marginTop: 8 }}><b>ROS</b>
                    <ul className="tight">{rosLines(note.ros).map((r) => (
                      <li key={r.sys}><span className="muted">{r.sys}:</span> {r.pos && <span>+{r.pos} </span>}{r.neg && <span className="muted">−{r.neg}</span>}</li>
                    ))}</ul>
                  </div>
                )}
                {NOTE_FIELDS.filter(([, k]) => note[k]).map(([label, k]) => (
                  <div key={k} style={{ marginTop: 6, fontSize: 13 }}><b>{label}:</b> {note[k]}</div>
                ))}
                {(note.to_discuss_with_provider || []).length > 0 && (
                  <div style={{ marginTop: 6, fontSize: 13 }}><b>To discuss:</b> {note.to_discuss_with_provider.join("; ")}</div>
                )}
              </>
            ) : <p className="muted">No note.</p>}

            {diff && (
              <div style={{ marginTop: 12, borderTop: "1px solid var(--line)", paddingTop: 10 }}>
                <b>Consider</b>
                <ol>{(diff.diagnoses_to_consider || []).map((x, i) => <li key={i}>{x.diagnosis} <span className="muted">({x.likelihood}%)</span></li>)}</ol>
                {(diff.do_not_miss || []).length > 0 && (<><b>Do not miss</b>
                  <ol>{diff.do_not_miss.map((x, i) => <li key={i}>{x.diagnosis} <span className="muted">({x.likelihood}%)</span></li>)}</ol></>)}
                {(diff.next_steps || []).length > 0 && (<><b>Next steps</b>
                  <ol>{diff.next_steps.map((x, i) => <li key={i}>{x.step}</li>)}</ol></>)}
              </div>
            )}
          </div>

          <div className="card">
            <div className="between"><h3>Evaluation</h3>
              <div className="flex" style={{ gap: 8 }}>
                <button className="btn sm" disabled={grading} onClick={regrade}>{grading ? "Grading…" : ev ? "Re-grade" : "Grade"}</button>
                <CopyBtn label="eval" copied={copied} onCopy={() => copy("eval", copyEval(ev))} disabled={!ev} />
              </div></div>
            <EvalView ev={ev} />
          </div>

          <div style={{ marginTop: -4 }}>
            <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".4px", marginBottom: 6 }}>Provenance</div>
            <div className="prov-subtle">
              {[
                ["when", fmtWhen(d.created_at)], ["user", d.created_by || "—"],
                ["experiment", d.experiment_id || "(scratch/none)"], ["case", d.case_id || "—"], ["case_hash", d.case_content_hash || "—"],
                ["chief_complaint", d.chief_complaint], ["classified_cc", d.classified_cc], ["graph_id", d.graph_id],
                ["system", d.system], ["sut_version", d.sut_version || "—"], ["sut_families", JSON.stringify(d.sut_internal_families)],
                ["emulator", d.emulator_model], ["rep", d.rep_index], ["persona", d.comm_style], ["seed", d.seed],
                ["service_type", d.service_type || "—"], ["model_set", d.model_set || "—"],
                ["prompts_commit", d.prompts_commit ? <CommitLink sha={d.prompts_commit} icon={false} /> : "— (unpinned)"],
                ["prompt_hashes", JSON.stringify(p.prompt_hashes)],
                ["timings", `interview=${secs(d.interview_ms)} · sut_gen=${secs(d.sut_gen_ms)} · turns=${d.n_turns}`],
                ["path_trace", p.mari_path_trace ? "[internal-only — aggregated before export]" : "(none — baseline)"],
              ].map(([k, v]) => <div key={k}><span className="prov-k">{k}</span> {React.isValidElement(v) ? v : String(v ?? "—")}</div>)}
            </div>
          </div>
        </div>

        {/* RIGHT — transcript / facts tabs */}
        <div className="run-col">
          {(() => {
            const facts = p.facts_captured || [];
            const presetFacts = facts.filter(isPresetFact);
            const mainFacts = facts.filter((f) => !isPresetFact(f));
            const tab = rightTab === "rootcause" && !rcView.showTab ? "transcript" : rightTab;
            const onFacts = tab === "facts", onRc = tab === "rootcause", onTr = tab === "transcript";
            // Consecutive facts grouped by (question, node_id); chronological order preserved.
            const factGroups = groupFacts(mainFacts, p.transcript);
            return (
              <div className="card">
                <div className="between" style={{ marginBottom: 12 }}>
                  <div className="flex" style={{ gap: 6 }}>
                    <button className={`btn sm ${onTr ? "primary" : ""}`} onClick={() => setRightTab("transcript")}>Transcript</button>
                    <button className={`btn sm ${onFacts ? "primary" : ""}`} onClick={() => setRightTab("facts")}>Facts{facts.length ? ` (${facts.length})` : ""}</button>
                    {rcView.showTab && (
                      <button className={`btn sm ${onRc ? "primary" : ""}`} onClick={() => setRightTab("rootcause")}>Root cause</button>
                    )}
                  </div>
                  {onFacts && <CopyBtn label="facts" copied={copied} onCopy={() => copy("facts", copyFacts(facts, p.transcript))} disabled={!facts.length} />}
                  {onTr && <CopyBtn label="transcript" copied={copied} onCopy={() => copy("transcript", copyTranscript(p.transcript))} disabled={!p.transcript} />}
                </div>

                {onRc && <RootCauseTab view={rcView} onReanalyze={reanalyze} reanalyzing={reanalyzing} reanalyzeError={rcErr} onViewTranscript={viewInTranscript} />}

                {onTr && (p.transcript ? (
                  <div className="chat qa-list" ref={qaRef}>{pairs.map(({ question: q, answer: a }, i) => (
                    <div key={i} data-qa={i} className={`qa${hl?.i === i ? " qa-hl" : ""}`}>
                      {q && <div className="qa-q">{q.text}</div>}
                      {/* A reply that matched the question's MCQ renders as the read-only MCQ. */}
                      {a && (a.mcqView ? (
                        <div className="qa-a">
                          <TranscriptMCQ options={a.mcqView.options} selection={a.mcqView.selection} selectedIds={a.mcqView.selectedIds} />
                        </div>
                      ) : <div className="qa-a">{a.text}</div>)}
                    </div>
                  ))}</div>
                ) : <p className="muted">No transcript.</p>)}

                {onFacts && (
                  (mainFacts.length || presetFacts.length) ? (
                    <>
                      {factGroups.map((g, gi) => (
                        <div key={gi} style={{ marginTop: gi ? 14 : 2 }}>
                          <div className="fact-head">
                            {g.question && <span className="fact-q">{g.question}</span>}
                            <span className="fact-node mono">{nodeLabel(g.node_id)}</span>
                          </div>
                          {g.facts.map((f, i) => (
                            <div key={i} style={{ padding: "3px 6px", borderRadius: 6, fontSize: 13,
                                                  background: f.changed ? "rgba(217,164,6,0.12)" : undefined }}>
                              <span className="fact-key mono">{f.key}:</span> <span className={factValueCls(f.value)}>{fmtFactValue(f.value)}</span>
                              {f.changed && <span className="chip warn" style={{ marginLeft: 6 }} title={`was: ${fmtFactValue(f.prev_value)}`}>changed · was {fmtFactValue(f.prev_value)}</span>}
                            </div>
                          ))}
                        </div>
                      ))}
                      {mainFacts.length === 0 && <p className="muted" style={{ fontSize: 13 }}>No interview facts captured.</p>}
                      {presetFacts.length > 0 && (
                        <details style={{ marginTop: 10 }}>
                          <summary className="muted" style={{ cursor: "pointer", fontSize: 12 }}>Config / preset facts ({presetFacts.length})</summary>
                          <div style={{ marginTop: 6 }}>
                            {presetFacts.map((f, i) => (
                              <div key={i} style={{ fontSize: 13 }}><span className="fact-key mono">{f.key}:</span> <span className={factValueCls(f.value)}>{fmtFactValue(f.value)}</span></div>
                            ))}
                          </div>
                        </details>
                      )}
                    </>
                  ) : <p className="muted">No facts captured (Mari only).</p>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    </>
  );
}

function RubricSection({ label, icon, rub }) {
  if (rub === undefined || rub === null) return null;   // stage skipped (empty list) — nothing to show
  if (rub.error) return <div className="banner bad" style={{ marginTop: 10 }}>{label} grading failed: {rub.detail}</div>;
  return (
    <div style={{ marginTop: 12 }}>
      <div className="flex" style={{ alignItems: "center", gap: 8 }}>{icon}<b>{label}</b>{pctChip(rub.captured, rub.total, rub.score_pct)}</div>
      <ul style={{ listStyle: "none", paddingLeft: 0, marginTop: 6 }}>
        {(rub.items || []).map((it, i) => (
          <li key={i} style={{ marginBottom: 6 }}>
            {it.captured ? <IconCheck size={15} style={{ color: "var(--ok)", verticalAlign: "-3px" }} />
              : <IconX size={15} style={{ color: "var(--bad)", verticalAlign: "-3px" }} />}
            {" "}{it.item}<div className="muted" style={{ fontSize: 12 }}>{it.rationale}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EvalView({ ev }) {
  if (!ev) return <p className="muted" style={{ marginTop: 4 }}>Not yet graded.</p>;
  const dx = ev.diagnosis;
  // pre-split runs stored a single combined rubric under `rubric`; new runs store redflags + must_capture.
  const legacy = ev.redflags === undefined && ev.must_capture === undefined;
  return (
    <>
      <div className="muted" style={{ fontSize: 11, marginBottom: 10 }}>grader: {ev.evaluator_model} · {fmtWhen(ev.graded_at)}</div>
      {legacy ? <RubricSection label="Must-capture (legacy)" icon={null} rub={ev.rubric} />
        : <>
            <RubricSection label="Red Flags" icon={<RedFlag size={15} />} rub={ev.redflags} />
            <RubricSection label="Must Capture" icon={null} rub={ev.must_capture} />
            {ev.redflags == null && ev.must_capture == null && <p className="muted">No rubric on this case.</p>}
          </>}
      {dx?.error ? <div className="banner bad" style={{ marginTop: 10 }}>Diagnosis grading failed: {dx.detail}</div>
        : dx ? (
          <div style={{ marginTop: 12 }}>
            <div className="flex" style={{ alignItems: "center", gap: 8 }}><b>Goal diagnosis</b>{tierChip(dx.tier)}</div>
            <div style={{ marginTop: 4 }}>Goal: <b>{dx.goal}</b>
              {dx.matched_label && <> · matched <span className="mono">{dx.matched_label}</span>{dx.rank ? ` (rank ${dx.rank})` : ""}</>}</div>
            {dx.rationale && <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{dx.rationale}</div>}
          </div>
        ) : <p className="muted" style={{ marginTop: 10 }}>No goal diagnosis on this case.</p>}
    </>
  );
}
